'use client';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Skeleton } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { MdDragIndicator } from 'react-icons/md';

import {
  useCreateJobDeliverableMutation,
  useCreateJobDeliverablePoolMutation,
  useDeleteJobDeliverableMutation,
  useGetJobDeliverablesQuery,
  usePatchJobDeliverableMutation,
  usePatchJobDeliverablePoolMutation,
} from '@/features/jobs/api/jobsApi';
import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import {
  ServiceTypeModalForm,
  ServiceTypeSelect,
} from '@/features/service-types/components';
import { useAutosaveMutation } from '@web/hooks';
import {
  Badge,
  InfoIcon,
  InlineButton,
  MainButton,
  Modal,
  OptionsMenu,
  PlusIcon,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobDeliverablesStep.module.scss';

import type { JobFormMode, JobSummaryDraft } from './types';
import type {
  JobDeliverable,
  JobDeliverableServiceTypePool,
} from '@/features/jobs/types';

type JobDeliverablesStepProps = {
  mode: JobFormMode;
  totalSteps: number;
  summary: JobSummaryDraft;
  isSaving: boolean;
  readOnly?: boolean;
  onPatchDraft: (
    patch: Partial<JobSummaryDraft>,
    options?: { persist?: boolean },
  ) => Promise<boolean>;
  onContinue: () => void;
};

type DurationUnit = 'days' | 'weeks' | 'months';

const sortRows = (rows: JobDeliverable[]) =>
  [...rows].sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));

const getPool = (row: JobDeliverable, serviceTypeId: string) =>
  row.service_type_pools?.find(
    (pool) => pool.service_type.id === serviceTypeId,
  ) ?? null;

type SortableRowRenderProps = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
};

function SortableRow({
  id,
  children,
}: {
  id: string;
  children: (dragHandle: SortableRowRenderProps) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {children({ attributes, listeners })}
    </tr>
  );
}

function HoursCell({
  value,
  readOnly,
  autosaveKey,
  onSave,
  onSaveError,
}: {
  value: number;
  readOnly: boolean;
  autosaveKey: string;
  onSave: (next: number) => Promise<void>;
  onSaveError: (error: unknown) => void;
}) {
  const [local, setLocal] = useState(value);

  useAutosaveMutation({
    value: local,
    persistedValue: value,
    contextKey: autosaveKey,
    onSave,
    onError: onSaveError,
    delay: 350,
    enabled: !readOnly,
  });

  if (readOnly) {
    return <span>{value}</span>;
  }

  return (
    <SmartInput
      type="number"
      name="deliverable-hours"
      label="Hrs"
      value={local}
      readOnly={readOnly}
      min={0}
      onChange={(next) => {
        if (readOnly) return;
        setLocal(next ?? 0);
      }}
      formatOnBlur
    />
  );
}

export function JobDeliverablesStep({
  mode,
  totalSteps,
  summary,
  isSaving,
  readOnly: readOnlyProp,
  onPatchDraft,
  onContinue,
}: JobDeliverablesStepProps) {
  const readOnly = readOnlyProp ?? mode !== 'new';
  const canEdit = !readOnly;
  const isProject = summary.jobKind === 'project';
  const jobId = summary.id ?? '';
  const {
    data,
    refetch,
    isLoading: isDeliverablesLoadingInitial,
    isFetching: isDeliverablesFetching,
  } = useGetJobDeliverablesQuery(
    { jobId, params: { page_size: 100 } },
    { skip: !summary.id, refetchOnMountOrArgChange: true },
  );
  const { data: serviceTypesResponse, refetch: refetchServiceTypes } =
    useGetServiceTypesQuery({
      page_size: 100,
      show_archived: false,
    });
  const [createDeliverable] = useCreateJobDeliverableMutation();
  const [patchDeliverable] = usePatchJobDeliverableMutation();
  const [deleteDeliverable] = useDeleteJobDeliverableMutation();
  const [createPool] = useCreateJobDeliverablePoolMutation();
  const [patchPool] = usePatchJobDeliverablePoolMutation();
  const [rows, setRows] = useState<JobDeliverable[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [serviceTypeToAdd, setServiceTypeToAdd] = useState('');
  const [durationUnits, setDurationUnits] = useState<
    Record<string, DurationUnit>
  >({});
  const [serviceTypeModalOpen, setServiceTypeModalOpen] = useState(false);

  useEffect(() => {
    const payload = data?.data as
      | { results?: JobDeliverable[] }
      | JobDeliverable[]
      | null;
    if (!payload) return;
    const nextRows = sortRows(
      Array.isArray(payload) ? payload : (payload.results ?? []),
    );
    const nextUnits = nextRows.reduce<Record<string, DurationUnit>>(
      (acc, row) => {
        if (
          row.duration_unit === 'days' ||
          row.duration_unit === 'weeks' ||
          row.duration_unit === 'months'
        ) {
          acc[row.id] = row.duration_unit;
        } else {
          acc[row.id] = 'days';
        }
        return acc;
      },
      {},
    );
    // eslint-disable-next-line
    setRows(nextRows);
    setDurationUnits(nextUnits);
  }, [data?.data]);

  const serviceTypeLabels = useMemo(
    () =>
      new Map(
        (serviceTypesResponse?.data?.results ?? []).map((s) => [s.id, s.label]),
      ),
    [serviceTypesResponse?.data?.results],
  );

  const poolServiceTypeLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const row of rows) {
      for (const pool of row.service_type_pools ?? []) {
        labels.set(pool.service_type.id, pool.service_type.label);
      }
    }
    return labels;
  }, [rows]);

  const serviceColumns = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];

    for (const id of summary.serviceTypeIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }

    for (const row of rows) {
      for (const pool of row.service_type_pools ?? []) {
        const id = pool.service_type.id;
        if (!seen.has(id)) {
          seen.add(id);
          ids.push(id);
        }
      }
    }

    return ids.map((id) => ({
      id,
      label: serviceTypeLabels.get(id) ?? poolServiceTypeLabels.get(id) ?? id,
    }));
  }, [poolServiceTypeLabels, rows, serviceTypeLabels, summary.serviceTypeIds]);

  const dependencyOptions = useMemo(
    () => [
      { value: '', label: 'No dependency' },
      ...rows.map((r) => ({ value: r.id, label: r.name })),
    ],
    [rows],
  );

  const totals = useMemo(() => {
    const byService = new Map<string, number>();
    for (const row of rows) {
      for (const service of serviceColumns) {
        byService.set(
          service.id,
          (byService.get(service.id) ?? 0) +
            (getPool(row, service.id)?.estimated_hours ?? 0),
        );
      }
    }
    return byService;
  }, [rows, serviceColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const saveDeliverable = useCallback(
    async (deliverableId: string, payload: Record<string, unknown>) => {
      if (!canEdit) return;
      try {
        const response = await patchDeliverable({
          deliverableId,
          payload,
        }).unwrap();
        if (response.data) {
          setRows((current) =>
            sortRows(
              current.map((r) => (r.id === deliverableId ? response.data! : r)),
            ),
          );
        }
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [canEdit, patchDeliverable],
  );

  const onDragEnd = async (event: DragEndEvent) => {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setRows(reordered);
    const moved = reordered[newIndex];
    if (moved) await saveDeliverable(moved.id, { sort_order: newIndex + 1 });
  };

  const handleSelectServiceType = useCallback(
    async (value: string) => {
      setServiceTypeToAdd(value);
      if (!value || summary.serviceTypeIds.includes(value)) return;
      const saved = await onPatchDraft(
        { serviceTypeIds: [...summary.serviceTypeIds, value] },
        { persist: true },
      );
      if (saved) setServiceTypeToAdd('');
    },
    [onPatchDraft, summary.serviceTypeIds],
  );

  const isDuplicateDeliverableName = useCallback(
    (candidate: string) => {
      const normalized = candidate.trim().toLowerCase();
      if (!normalized) return false;
      return rows.some((row) => row.name.trim().toLowerCase() === normalized);
    },
    [rows],
  );

  const handleCreateDeliverable = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed || !canEdit || !summary.id) return;
    if (isDuplicateDeliverableName(trimmed)) {
      notify({
        type: 'error',
        message: 'A deliverable with this name already exists.',
      });
      return;
    }
    const payload = {
      name: trimmed,
      status: 'backlog',
      ...(isProject ? { duration: 1 } : {}),
    };
    const response = await createDeliverable({
      jobId: summary.id,
      payload,
    }).unwrap();
    if (response.data) {
      setRows((current) => sortRows([...current, response.data!]));
    }
    setNewName('');
    setIsAdding(false);
  }, [
    canEdit,
    createDeliverable,
    isDuplicateDeliverableName,
    isProject,
    newName,
    summary.id,
  ]);

  const isDeliverablesLoading =
    Boolean(summary.id) &&
    (isDeliverablesLoadingInitial || isDeliverablesFetching) &&
    rows.length === 0;

  return (
    <section className={styles.jobDeliverablesStep}>
      <div>
        <Badge label={`Step 2 of ${totalSteps}`} tone="default" size="lg" />
      </div>
      <div>
        <h2 className={styles.jobDeliverablesStep__title}>Plan the work</h2>
        <p className={styles.jobDeliverablesStep__subtitle}>
          Define deliverables and the hours required to complete them.
        </p>
      </div>
      <div className={styles.jobDeliverablesStep__panel}>
        <div className={styles.jobDeliverablesStep__panelHeader}>
          <div className={styles.jobDeliverablesStep__panelIntro}>
            <h3 className={styles.jobDeliverablesStep__panelTitle}>
              Deliverables + LOE
            </h3>
            <p className={styles.jobDeliverablesStep__panelText}>
              Set up deliverables, assign hours by service type, and let totals
              update instantly as you plan.
            </p>
          </div>
          <div className={styles.jobDeliverablesStep__controls}>
            <div className={styles.jobDeliverablesStep__serviceTypeSelect}>
              <ServiceTypeSelect
                value={serviceTypeToAdd}
                onChange={handleSelectServiceType}
                name="add-service-type"
                label="Service types"
                includeAllOption={false}
                minWidth={190}
                disabled={!canEdit}
                selectedLabel={serviceTypeLabels.get(serviceTypeToAdd)}
              />
            </div>

            {canEdit && (
              <SecondaryButton
                ariaLabel="Open service type modal"
                variant="medium"
                onClick={() => setServiceTypeModalOpen(true)}
              >
                <>
                  <PlusIcon size={14} />
                  &nbsp;Add Service Type
                </>
              </SecondaryButton>
            )}
          </div>
        </div>

        {isDeliverablesLoading ? (
          <div data-testid="job-deliverables-loading-skeleton">
            <Skeleton variant="rectangular" height={44} />
            <Skeleton variant="rectangular" height={230} />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => void onDragEnd(event)}
          >
            <table className={styles.jobDeliverablesStep__table}>
              <thead>
                <tr>
                  <th>Deliverable Name</th>
                  {serviceColumns.map((service) => (
                    <th key={service.id}>{service.label}</th>
                  ))}
                  <th>Total Hours</th>
                  <th />
                </tr>
              </thead>
              <SortableContext
                items={rows.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {rows.map((row) => {
                    const unit = durationUnits[row.id] ?? 'days';
                    const durationValue = Math.max(1, row.duration ?? 1);
                    const rowHours = serviceColumns.reduce(
                      (sum, service) =>
                        sum + (getPool(row, service.id)?.estimated_hours ?? 0),
                      0,
                    );
                    return (
                      <Fragment key={row.id}>
                        <SortableRow id={row.id}>
                          {({ attributes, listeners }) => (
                            <>
                              <td>
                                <div
                                  className={
                                    styles.jobDeliverablesStep__nameCell
                                  }
                                >
                                  <button
                                    type="button"
                                    aria-label="Drag deliverable row"
                                    className={
                                      styles.jobDeliverablesStep__dragHandle
                                    }
                                    {...attributes}
                                    {...listeners}
                                  >
                                    <MdDragIndicator size={18} />
                                  </button>
                                  <div
                                    className={
                                      styles.jobDeliverablesStep__nameEditor
                                    }
                                  >
                                    {editingId === row.id ? (
                                      <SmartInput
                                        type="text"
                                        name={`deliverable-name-${row.id}`}
                                        label=""
                                        value={row.name}
                                        readOnly={!canEdit}
                                        onChange={(value) =>
                                          setRows((current) =>
                                            current.map((r) =>
                                              r.id === row.id
                                                ? { ...r, name: value }
                                                : r,
                                            ),
                                          )
                                        }
                                        onBlur={() => {
                                          setEditingId(null);
                                          void saveDeliverable(row.id, {
                                            name: row.name,
                                          });
                                        }}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          canEdit && setEditingId(row.id)
                                        }
                                      >
                                        {row.name}
                                      </button>
                                    )}
                                  </div>
                                  <div
                                    className={
                                      styles.jobDeliverablesStep__nameActions
                                    }
                                  >
                                    <SecondaryButton
                                      ariaLabel="Toggle deliverable details"
                                      variant="mini"
                                      onClick={() =>
                                        setExpandedId((current) =>
                                          current === row.id ? null : row.id,
                                        )
                                      }
                                    >
                                      <InfoIcon size={16} />
                                    </SecondaryButton>
                                  </div>
                                </div>
                              </td>
                              {serviceColumns.map((service) => (
                                <td key={`${row.id}:${service.id}`}>
                                  <HoursCell
                                    key={`${row.id}:${service.id}:${
                                      getPool(row, service.id)?.id ?? 'new'
                                    }:${
                                      getPool(row, service.id)
                                        ?.estimated_hours ?? 0
                                    }`}
                                    value={
                                      getPool(row, service.id)
                                        ?.estimated_hours ?? 0
                                    }
                                    readOnly={!canEdit}
                                    autosaveKey={`${row.id}:${service.id}:hours`}
                                    onSave={async (next) => {
                                      const currentPool = getPool(
                                        row,
                                        service.id,
                                      );
                                      let nextPool: JobDeliverableServiceTypePool | null =
                                        null;
                                      if (!currentPool) {
                                        const response = await createPool({
                                          deliverableId: row.id,
                                          payload: {
                                            service_type_id: service.id,
                                            estimated_hours: next,
                                          },
                                        }).unwrap();
                                        nextPool = response.data ?? null;
                                      } else {
                                        const response = await patchPool({
                                          poolId: currentPool.id,
                                          payload: { estimated_hours: next },
                                        }).unwrap();
                                      }
                                      setRows((current) =>
                                        current.map((r) => {
                                          if (r.id !== row.id) return r;
                                          const pools =
                                            r.service_type_pools ?? [];
                                          if (!currentPool && nextPool) {
                                            return {
                                              ...r,
                                              service_type_pools: [
                                                ...pools,
                                                nextPool,
                                              ],
                                            };
                                          }
                                          if (currentPool && nextPool) {
                                            return {
                                              ...r,
                                              service_type_pools: pools.map(
                                                (p) =>
                                                  p.id === currentPool.id
                                                    ? nextPool
                                                    : p,
                                              ),
                                            };
                                          }
                                          return r;
                                        }),
                                      );
                                    }}
                                    onSaveError={(error) => {
                                      notify({
                                        type: 'error',
                                        message: getErrorMessage(error),
                                      });
                                    }}
                                  />
                                </td>
                              ))}
                              <td>{rowHours}</td>
                              <td
                                className={
                                  styles.jobDeliverablesStep__actionsCell
                                }
                              >
                                <OptionsMenu
                                  ariaLabel="Deliverable actions"
                                  actions={
                                    canEdit
                                      ? [
                                          {
                                            key: 'delete',
                                            label: 'Delete',
                                            tone: 'danger',
                                            onSelect: () =>
                                              void deleteDeliverable(row.id)
                                                .unwrap()
                                                .then(() =>
                                                  setRows((current) =>
                                                    current.filter(
                                                      (r) => r.id !== row.id,
                                                    ),
                                                  ),
                                                ),
                                          },
                                        ]
                                      : []
                                  }
                                />
                              </td>
                            </>
                          )}
                        </SortableRow>
                        {expandedId === row.id ? (
                          <tr>
                            <td colSpan={serviceColumns.length + 3}>
                              <div
                                className={
                                  styles.jobDeliverablesStep__detailsContent
                                }
                              >
                                <SmartInput
                                  type="textarea"
                                  name={`deliverable-description-${row.id}`}
                                  label="Description"
                                  value={row.description ?? ''}
                                  readOnly={!canEdit}
                                  onChange={(value) =>
                                    setRows((current) =>
                                      current.map((r) =>
                                        r.id === row.id
                                          ? { ...r, description: value }
                                          : r,
                                      ),
                                    )
                                  }
                                  onBlur={() =>
                                    void saveDeliverable(row.id, {
                                      description: row.description,
                                    })
                                  }
                                />
                                <div
                                  className={
                                    styles.jobDeliverablesStep__detailsRow
                                  }
                                >
                                  {isProject ? (
                                    <>
                                      <SmartInput
                                        type="number"
                                        name={`deliverable-duration-${row.id}`}
                                        label="Duration"
                                        value={durationValue}
                                        min={1}
                                        readOnly={!canEdit}
                                        onChange={(value) => {
                                          const safe = Math.max(1, value ?? 1);
                                          setRows((current) =>
                                            current.map((r) =>
                                              r.id === row.id
                                                ? { ...r, duration: safe }
                                                : r,
                                            ),
                                          );
                                        }}
                                        onBlur={() =>
                                          void saveDeliverable(row.id, {
                                            duration: row.duration,
                                          })
                                        }
                                      />
                                      <SmartInput
                                        type="select"
                                        name={`deliverable-duration-unit-${row.id}`}
                                        label="Unit"
                                        value={unit}
                                        options={[
                                          { value: 'days', label: 'Days' },
                                          { value: 'weeks', label: 'Weeks' },
                                          { value: 'months', label: 'Months' },
                                        ]}
                                        readOnly={!canEdit}
                                        onChange={(value) => {
                                          const nextUnit =
                                            value as DurationUnit;
                                          if (unit === nextUnit) {
                                            return;
                                          }
                                          setDurationUnits((current) => ({
                                            ...current,
                                            [row.id]: nextUnit,
                                          }));
                                          setRows((current) =>
                                            current.map((r) =>
                                              r.id === row.id
                                                ? {
                                                    ...r,
                                                    duration_unit: nextUnit,
                                                  }
                                                : r,
                                            ),
                                          );
                                          void saveDeliverable(row.id, {
                                            duration_unit: nextUnit,
                                          });
                                        }}
                                      />
                                    </>
                                  ) : null}
                                  <SmartInput
                                    type="select"
                                    name={`deliverable-dependency-${row.id}`}
                                    label="Dependency"
                                    value={row.parent_deliverable_id ?? ''}
                                    options={dependencyOptions.filter(
                                      (item) => item.value !== row.id,
                                    )}
                                    readOnly={!canEdit}
                                    onChange={(value) => {
                                      const nextParentId =
                                        String(value || '') || null;
                                      if (
                                        row.parent_deliverable_id ===
                                        nextParentId
                                      ) {
                                        return;
                                      }
                                      setRows((current) =>
                                        current.map((r) =>
                                          r.id === row.id
                                            ? {
                                                ...r,
                                                parent_deliverable_id:
                                                  nextParentId,
                                              }
                                            : r,
                                        ),
                                      );
                                      void saveDeliverable(row.id, {
                                        parent_deliverable_id: nextParentId,
                                      });
                                    }}
                                  />
                                </div>
                                <SmartInput
                                  type="textarea"
                                  name={`deliverable-notes-${row.id}`}
                                  label="Internal notes"
                                  value={row.internal_notes ?? ''}
                                  readOnly={!canEdit}
                                  onChange={(value) =>
                                    setRows((current) =>
                                      current.map((r) =>
                                        r.id === row.id
                                          ? { ...r, internal_notes: value }
                                          : r,
                                      ),
                                    )
                                  }
                                  onBlur={() =>
                                    void saveDeliverable(row.id, {
                                      internal_notes: row.internal_notes,
                                    })
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                  <tr>
                    <td>
                      {isAdding ? (
                        <SmartInput
                          type="text"
                          name="new-deliverable-name"
                          label="Deliverable name"
                          value={newName}
                          readOnly={!canEdit}
                          onChange={setNewName}
                          onBlur={async () => {
                            await handleCreateDeliverable();
                          }}
                          onKeyDown={async (event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            await handleCreateDeliverable();
                          }}
                        />
                      ) : (
                        <InlineButton
                          ariaLabel="Add deliverable"
                          onClick={() => {
                            if (!canEdit) return;
                            setIsAdding(true);
                          }}
                        >
                          <>
                            <PlusIcon />
                            &nbsp;Add deliverable
                          </>
                        </InlineButton>
                      )}
                    </td>
                    {serviceColumns.map((service) => (
                      <td key={`empty:${service.id}`} />
                    ))}
                    <td />
                    <td />
                  </tr>
                  <tr>
                    <td>Totals</td>
                    {serviceColumns.map((service) => (
                      <td key={`totals:${service.id}`}>
                        {totals.get(service.id) ?? 0}
                      </td>
                    ))}
                    <td>
                      {Array.from(totals.values()).reduce(
                        (sum, value) => sum + value,
                        0,
                      )}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        )}
      </div>
      <footer className={styles.jobDeliverablesStep__footer}>
        <MainButton
          ariaLabel="Continue"
          disabled={isSaving}
          isLoading={isSaving}
          onClick={onContinue}
        >
          Continue
        </MainButton>
      </footer>
      <Modal
        open={serviceTypeModalOpen}
        onClose={() => setServiceTypeModalOpen(false)}
        width="min(720px, 96vw)"
      >
        <div className="card-container-glass">
          <ServiceTypeModalForm
            mode="create"
            onCancel={() => setServiceTypeModalOpen(false)}
            onSuccess={() => {
              setServiceTypeModalOpen(false);
              void refetchServiceTypes();
            }}
          />
        </div>
      </Modal>
    </section>
  );
}
